import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../../lib/api";
import { Panel, Overline, Btn, Pill, Icon, PageLoader, ProgressBar, EmptyState, gradFor } from "../../components/ui-kit";
import AskAIPanel from "../../components/AskAIPanel";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const MODES = [
  { id: "learn", label: "Learn", icon: "BookOpen", grad: "linear-gradient(135deg,#2f86f5,#1b5fc0)", desc: "Video lesson" },
  { id: "snapshot", label: "Snapshot", icon: "Zap", grad: "linear-gradient(135deg,#33c7e2,#0f97b3)", desc: "60-sec recap" },
  { id: "flashcards", label: "Flashcards", icon: "Layers", grad: "linear-gradient(135deg,#f7c65e,#e09a1f)", desc: "Flip & revise" },
  { id: "quiz", label: "Quiz", icon: "CircleHelp", grad: "linear-gradient(135deg,#28c98f,#159669)", desc: "Test yourself" },
  { id: "askai", label: "Ask AI", icon: "Sparkles", grad: "linear-gradient(135deg,#8a6df0,#5f3fd0)", desc: "Tutor chat" },
];

export default function TopicPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("learn");
  useEffect(() => { api.get(`/academics/topic/${id}`).then(({ data }) => setData(data)); }, [id]);
  if (!data) return <PageLoader />;
  const { topic, subject, chapter, video, flashcards, quiz, questions } = data;
  const markProgress = (section, pct) => api.post("/student/progress", { topic_id: id, section, progress_pct: pct }).catch(() => {});

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6">
      <Link to={`/learn/subject/${subject.id}`} className="inline-flex items-center gap-1 text-sm text-mim-muted hover:text-mim-navy mb-3"><Icon name="ArrowLeft" className="w-4 h-4" />{subject.name} · {chapter.name}</Link>

      {/* Topic hero */}
      <div className="relative rounded-[28px] p-6 sm:p-7 text-white overflow-hidden mb-5" style={{ background: gradFor(subject.color) }}>
        <div className="absolute inset-0 dotgrid opacity-50" />
        <div className="relative flex items-start justify-between gap-4">
          <div>
            <Pill tone="white" icon={subject.icon}>{subject.name}</Pill>
            <h1 className="text-3xl sm:text-4xl font-bold mt-3 tracking-tight">{topic.name}</h1>
            <p className="text-white/75 text-sm mt-2 max-w-lg">{topic.intro}</p>
          </div>
          <Pill tone="white" icon="Flag">{topic.exam_priority} priority</Pill>
        </div>
      </div>

      {/* Learning mode tiles */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-6">
        {MODES.map((m) => (
          <button key={m.id} onClick={() => setMode(m.id)} data-testid={`tab-${m.id}`}
            className={cn("relative rounded-2xl p-3.5 text-left overflow-hidden transition-all", mode === m.id ? "text-white shadow-lg scale-[1.02]" : "bg-white border border-mim-line text-mim-navy hover:border-mim-blue/40 hover-lift")}
            style={mode === m.id ? { background: m.grad } : {}}>
            <Icon name={m.icon} className={cn("w-5 h-5", mode === m.id ? "" : "text-mim-blue")} />
            <div className="font-semibold text-sm mt-2">{m.label}</div>
            <div className={cn("text-[10px] mt-0.5", mode === m.id ? "text-white/75" : "text-mim-muted")}>{m.desc}</div>
          </button>
        ))}
      </div>

      <div className="animate-fade-up" key={mode}>
        {mode === "learn" && <LearnMode topic={topic} video={video} onDone={() => { markProgress("lesson", 40); toast.success("Lesson marked as read"); }} />}
        {mode === "snapshot" && <SnapshotMode topic={topic} onQuiz={() => setMode("quiz")} onAsk={() => setMode("askai")} onView={() => markProgress("snapshot", 60)} />}
        {mode === "flashcards" && <FlashcardsMode cards={flashcards} />}
        {mode === "quiz" && <QuizMode quiz={quiz} questions={questions} onAsk={() => setMode("askai")} />}
        {mode === "askai" && <Panel className="p-3 sm:p-4"><AskAIPanel topicId={id} topicName={topic.name} compact /></Panel>}
      </div>
    </div>
  );
}

function LearnMode({ topic, video, onDone }) {
  return (
    <div className="space-y-5">
      <div className="rounded-3xl overflow-hidden border border-mim-line shadow-mim bg-black">
        {video ? (
          <div className="aspect-video"><iframe className="w-full h-full" src={`https://www.youtube.com/embed/${video.youtube}`} title={video.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
        ) : (
          <div className="aspect-video bg-mim-soft flex flex-col items-center justify-center text-mim-muted"><Icon name="Clapperboard" className="w-12 h-12 mb-2" /><span className="text-sm font-semibold">🎬 Video coming soon</span></div>
        )}
      </div>
      {video && <div className="flex items-center justify-between"><div><h4 className="font-bold text-mim-navy">{video.title}</h4><p className="text-xs text-mim-muted mt-0.5">{video.duration} · {video.views} views</p></div><Btn onClick={onDone} icon="Check">Mark as read</Btn></div>}
    </div>
  );
}

function SnapshotMode({ topic, onQuiz, onAsk, onView }) {
  const s = topic.snapshot;
  useEffect(() => { onView(); }, []); // eslint-disable-line
  if (!s) return <Panel tint="soft"><EmptyState icon="Zap" title="Snapshot coming soon" subtitle="This topic's visual snapshot hasn't been generated yet. Your teacher or admin can create it from the admin Content Studio." tone="cyan" action={<Btn variant="ai" onClick={onAsk} icon="Sparkles">Ask AI instead</Btn>} /></Panel>;
  return (
    <div className="space-y-4">
      <div className="rounded-3xl p-7 text-white text-center relative overflow-hidden" style={{ background: "linear-gradient(135deg,#1d3155,#14213D)" }}>
        <div className="absolute inset-0 dotgrid opacity-50" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-mim-cyan mb-3"><Icon name="Zap" className="w-3.5 h-3.5" />Learn in 60 seconds</div>
          <p className="text-lg text-white/90 max-w-xl mx-auto leading-relaxed">{s.one_line}</p>
          {s.formula && s.formula !== "-" && <div className="inline-block mt-5 glass rounded-2xl px-7 py-4"><div className="text-[10px] font-bold uppercase tracking-widest text-mim-cyan mb-1">Key Formula</div><div className="text-2xl font-bold tracking-wide">{s.formula}</div></div>}
        </div>
      </div>

      {Array.isArray(s.overview) && s.overview.length > 0 && (
        <Panel>
          <div className="flex items-center gap-2 mb-3"><Icon name="BookOpenText" className="w-4 h-4 text-mim-blue" /><Overline className="text-mim-blue">Full Explanation</Overline></div>
          <div className="space-y-3">{s.overview.map((p, i) => <p key={i} className="text-[15px] text-mim-navy/90 leading-relaxed">{p}</p>)}</div>
        </Panel>
      )}

      {topic.images && topic.images.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3">
          {topic.images.map((img, i) => (
            <figure key={i} className="rounded-2xl overflow-hidden border border-mim-line bg-white">
              <img src={img.url} alt={img.title || topic.name} className="w-full object-cover" loading="lazy" />
              {img.title && <figcaption className="text-xs text-mim-muted p-2">{img.title}</figcaption>}
            </figure>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-3xl p-6 bg-mim-soft border border-mim-blue/12">
          <div className="flex items-center gap-2 mb-3"><Icon name="Key" className="w-4 h-4 text-mim-blue" /><Overline className="text-mim-blue">Key Ideas</Overline></div>
          <ul className="space-y-2.5">{(s.key_ideas || []).map((k, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-mim-navy"><span className="w-5 h-5 rounded-full bg-mim-blue text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>{k}</li>)}</ul>
        </div>
        <div className="rounded-3xl p-6 bg-mim-green/8 border border-mim-green/15">
          <div className="flex items-center gap-2 mb-3"><Icon name="Sprout" className="w-4 h-4 text-mim-green" /><Overline className="text-mim-green">Real Life</Overline></div>
          <p className="text-sm text-mim-navy">{s.real_life}</p>
        </div>
        <div className="rounded-3xl p-6 bg-mim-coral/6 border border-mim-coral/15">
          <div className="flex items-center gap-2 mb-3"><Icon name="TriangleAlert" className="w-4 h-4 text-mim-coral" /><Overline className="text-mim-coral">Common Mistake</Overline></div>
          <p className="text-sm text-mim-navy">{s.common_mistake}</p>
        </div>
        <div className="rounded-3xl p-6 bg-mim-yellow/12 border border-mim-yellow/25">
          <div className="flex items-center gap-2 mb-3"><Icon name="Lightbulb" className="w-4 h-4 text-[#b07d13]" /><Overline className="text-[#b07d13]">Exam Focus</Overline></div>
          <p className="text-sm text-mim-navy">{s.exam_tip}</p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <Btn variant="green" onClick={onQuiz} className="flex-1" size="lg" icon="CircleHelp">Test yourself</Btn>
        <Btn variant="ai" onClick={onAsk} className="flex-1" size="lg" icon="Sparkles">Ask AI to explain</Btn>
      </div>
    </div>
  );
}

function FlashcardsMode({ cards }) {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [order, setOrder] = useState(cards.map((_, i) => i));
  const [mastered, setMastered] = useState({});
  if (cards.length === 0) return <Panel tint="soft"><EmptyState icon="Layers" title="No flashcards yet" subtitle="Your teacher may add some soon." tone="yellow" /></Panel>;
  const card = cards[order[idx]];
  const mark = (status) => { api.post("/flashcard/mark", { flashcard_id: card.id, status }).catch(() => {}); setMastered((m) => ({ ...m, [card.id]: status })); if (status === "mastered") toast.success("Mastered! 🎉"); next(); };
  const next = () => { setFlipped(false); setTimeout(() => setIdx((i) => (i + 1) % cards.length), 150); };
  const prev = () => { setFlipped(false); setTimeout(() => setIdx((i) => (i - 1 + cards.length) % cards.length), 150); };
  const shuffle = () => { setOrder([...order].sort(() => Math.random() - 0.5)); setIdx(0); setFlipped(false); };
  const masteredCount = Object.values(mastered).filter((s) => s === "mastered").length;

  return (
    <div className="max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-semibold text-mim-navy">Card {idx + 1} of {cards.length}</span>
        <div className="flex items-center gap-2"><Pill tone="green">{masteredCount} mastered</Pill><button onClick={shuffle} data-testid="shuffle-btn" className="p-2 rounded-lg bg-white border border-mim-line hover:border-mim-blue/40 text-mim-navy"><Icon name="Shuffle" className="w-4 h-4" /></button></div>
      </div>
      <ProgressBar value={((idx + 1) / cards.length) * 100} className="mb-5" />
      <div className="[perspective:1200px] mb-5">
        <div className={cn("card-flip relative w-full h-80 cursor-pointer", flipped && "flipped")} onClick={() => setFlipped(!flipped)} data-testid="flashcard">
          <div className="card-face absolute inset-0 bg-white border border-mim-line rounded-[28px] shadow-mimlg flex flex-col items-center justify-center p-8 text-center">
            <Overline className="text-mim-blue mb-4">Question</Overline><p className="text-2xl font-semibold text-mim-navy">{card.front}</p>
            <span className="text-xs text-mim-muted mt-8 inline-flex items-center gap-1"><Icon name="MousePointerClick" className="w-3.5 h-3.5" />Tap to reveal</span>
          </div>
          <div className="card-face card-back absolute inset-0 rounded-[28px] shadow-mimlg flex flex-col items-center justify-center p-8 text-center text-white" style={{ background: "linear-gradient(135deg,#1d3155,#14213D)" }}>
            <Overline className="text-mim-cyan mb-4">Answer</Overline><p className="text-xl font-medium">{card.back}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={prev} className="p-3.5 rounded-2xl bg-white border border-mim-line hover:border-mim-blue/40 text-mim-navy" data-testid="flashcard-prev"><Icon name="ChevronLeft" className="w-5 h-5" /></button>
        <Btn variant="coral" onClick={() => mark("difficult")} className="flex-1" size="lg" icon="Bookmark" data-testid="flashcard-difficult">Difficult</Btn>
        <Btn variant="green" onClick={() => mark("mastered")} className="flex-1" size="lg" icon="Check" data-testid="flashcard-mastered">Mastered</Btn>
        <button onClick={next} className="p-3.5 rounded-2xl bg-white border border-mim-line hover:border-mim-blue/40 text-mim-navy" data-testid="flashcard-next"><Icon name="ChevronRight" className="w-5 h-5" /></button>
      </div>
    </div>
  );
}

function QuizMode({ quiz, questions, onAsk }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  if (!quiz || questions.length === 0) return <Panel tint="soft"><EmptyState icon="CircleHelp" title="No quiz available" subtitle="Check back after your teacher publishes one." tone="green" /></Panel>;
  const submit = async () => {
    if (Object.keys(answers).length < questions.length) { toast.error("Answer all questions first"); return; }
    setSubmitting(true);
    try { const { data } = await api.post("/quiz/submit", { quiz_id: quiz.id, answers }); setResult(data); toast.success(`${data.score}/${data.total} correct`); } finally { setSubmitting(false); }
  };

  if (result) {
    const pct = result.accuracy;
    return (
      <div className="space-y-4">
        <div className="rounded-[28px] p-8 text-center text-white relative overflow-hidden" style={{ background: pct >= 60 ? "linear-gradient(135deg,#28c98f,#159669)" : "linear-gradient(135deg,#f08a8a,#e05555)" }}>
          <div className="absolute inset-0 dotgrid opacity-50" />
          <div className="relative">
            <Icon name={pct >= 60 ? "PartyPopper" : "Target"} className="w-10 h-10 mx-auto mb-2" />
            <div className="text-5xl font-bold">{result.score}<span className="opacity-70 text-3xl">/{result.total}</span></div>
            <p className="text-white/85 mt-2">{pct}% accuracy · {pct >= 80 ? "Excellent!" : pct >= 60 ? "Good work!" : "Keep practising"}</p>
          </div>
        </div>
        {result.results.map((r, i) => (
          <Panel key={r.question_id} className={cn("border-l-4", r.is_correct ? "border-l-mim-green" : "border-l-mim-coral")}>
            <div className="flex items-start gap-2">
              <Icon name={r.is_correct ? "CircleCheck" : "CircleX"} className={cn("w-5 h-5 shrink-0 mt-0.5", r.is_correct ? "text-mim-green" : "text-mim-coral")} />
              <div className="flex-1">
                <p className="font-semibold text-mim-navy text-sm">{i + 1}. {r.question}</p>
                <div className="text-sm mt-2 space-y-1"><div className="text-mim-muted">Your answer: <span className={cn("font-semibold", r.is_correct ? "text-mim-green" : "text-mim-coral")}>{r.selected}</span></div>{!r.is_correct && <div className="text-mim-muted">Correct: <span className="font-semibold text-mim-green">{r.correct_answer}</span></div>}</div>
                <div className="bg-mim-soft rounded-xl px-3 py-2 mt-2 text-sm text-mim-navy/90"><span className="font-semibold text-mim-blue">Why? </span>{r.explanation}</div>
              </div>
            </div>
          </Panel>
        ))}
        <div className="flex gap-3"><Btn variant="secondary" onClick={() => { setResult(null); setAnswers({}); }} className="flex-1" icon="RotateCcw">Retry</Btn><Btn variant="ai" onClick={onAsk} className="flex-1" icon="Sparkles">Ask AI what I got wrong</Btn></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {questions.map((q, i) => (
        <Panel key={q.id}>
          <div className="flex items-center gap-2 mb-4"><span className="w-7 h-7 rounded-xl bg-mim-soft text-mim-blue text-xs font-bold flex items-center justify-center">{i + 1}</span><span className="text-[11px] font-bold uppercase tracking-widest text-mim-muted">Question {i + 1} of {questions.length}</span></div>
          <p className="font-semibold text-mim-navy mb-3">{q.question}</p>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {q.options.map((opt) => (
              <button key={opt} onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt }))} data-testid={`quiz-opt-${q.id}-${opt.slice(0, 6)}`}
                className={cn("text-left px-4 py-3.5 rounded-2xl border-2 text-sm font-medium transition-all flex items-center gap-3",
                  answers[q.id] === opt ? "border-mim-blue bg-mim-soft text-mim-blue" : "border-mim-line hover:border-mim-blue/40 text-mim-navy")}>
                <span className={cn("w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center", answers[q.id] === opt ? "border-mim-blue bg-mim-blue" : "border-mim-line")}>{answers[q.id] === opt && <Icon name="Check" className="w-3 h-3 text-white" />}</span>
                {opt}
              </button>
            ))}
          </div>
        </Panel>
      ))}
      <Btn onClick={submit} disabled={submitting} className="w-full" size="lg" icon="Send" data-testid="quiz-submit">{submitting ? "Submitting…" : "Submit Quiz"}</Btn>
    </div>
  );
}
